import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select", "shell", "button", "value", "popover", "search", "options", "option", "empty"]

  connect() {
    this.isOpen = false
    this.activeIndex = -1
    this.selectTarget.classList.add("hidden")
    this.shellTarget.classList.remove("hidden")
    this.syncSelection()
  }

  disconnect() {
    this.selectTarget.classList.remove("hidden")
    this.shellTarget.classList.add("hidden")
  }

  toggle(event) {
    event.preventDefault()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (this.buttonTarget.disabled) return

    this.isOpen = true
    this.popoverTarget.hidden = false
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.searchTarget.value = ""
    this.filterOptions("")
    this.positionPopover()

    const selectedIndex = this.visibleOptions.findIndex(option => option.dataset.value === this.selectTarget.value)
    this.setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
    requestAnimationFrame(() => this.searchTarget.focus({ preventScroll: true }))
  }

  close({ focusButton = false } = {}) {
    if (!this.isOpen) return

    this.isOpen = false
    this.popoverTarget.hidden = true
    this.buttonTarget.setAttribute("aria-expanded", "false")
    this.searchTarget.removeAttribute("aria-activedescendant")
    if (focusButton) this.buttonTarget.focus()
  }

  closeFromOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  buttonKeydown(event) {
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault()
      this.open()
    }
  }

  search(event) {
    this.filterOptions(event.currentTarget.value)
  }

  searchKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.setActiveIndex(this.activeIndex + 1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.setActiveIndex(this.activeIndex - 1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      const option = this.visibleOptions[this.activeIndex]
      if (option) this.chooseOption(option)
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.close({ focusButton: true })
    } else if (event.key === "Tab") {
      this.close()
    }
  }

  select(event) {
    event.preventDefault()
    this.chooseOption(event.currentTarget)
  }

  highlight(event) {
    const index = this.visibleOptions.indexOf(event.currentTarget)
    if (index >= 0) this.setActiveIndex(index)
  }

  chooseOption(option) {
    this.selectTarget.value = option.dataset.value
    this.selectTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.syncSelection()
    this.close({ focusButton: true })
  }

  syncSelection() {
    const selected = this.optionTargets.find(option => option.dataset.value === this.selectTarget.value) || this.optionTargets[0]
    if (!selected) return

    this.valueTarget.textContent = selected.dataset.label
    this.optionTargets.forEach(option => {
      const isSelected = option === selected
      option.classList.toggle("is-selected", isSelected)
      option.setAttribute("aria-selected", isSelected ? "true" : "false")
    })
  }

  filterOptions(query) {
    const normalizedQuery = query.trim().toLocaleLowerCase()

    this.optionTargets.forEach(option => {
      option.hidden = !option.dataset.label.toLocaleLowerCase().includes(normalizedQuery)
    })

    const hasOptions = this.visibleOptions.length > 0
    this.emptyTarget.hidden = hasOptions
    this.setActiveIndex(hasOptions ? 0 : -1)
  }

  setActiveIndex(index) {
    const options = this.visibleOptions
    this.optionTargets.forEach(option => option.classList.remove("is-active"))

    if (options.length === 0) {
      this.activeIndex = -1
      this.searchTarget.removeAttribute("aria-activedescendant")
      return
    }

    this.activeIndex = (index + options.length) % options.length
    const activeOption = options[this.activeIndex]
    activeOption.classList.add("is-active")
    this.searchTarget.setAttribute("aria-activedescendant", activeOption.id)
    this.scrollOptionIntoView(activeOption)
  }

  scrollOptionIntoView(option) {
    const options = this.optionsTarget
    const optionTop = option.offsetTop
    const optionBottom = optionTop + option.offsetHeight
    const visibleTop = options.scrollTop
    const visibleBottom = visibleTop + options.clientHeight

    if (optionTop < visibleTop) {
      options.scrollTop = optionTop
    } else if (optionBottom > visibleBottom) {
      options.scrollTop = optionBottom - options.clientHeight
    }
  }

  positionPopover() {
    const rect = this.buttonTarget.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openAbove = spaceBelow < 280 && rect.top > spaceBelow

    this.popoverTarget.style.top = openAbove ? "auto" : "calc(100% + 0.5rem)"
    this.popoverTarget.style.bottom = openAbove ? "calc(100% + 0.5rem)" : "auto"
    this.popoverTarget.style.transformOrigin = openAbove ? "bottom" : "top"
  }

  get visibleOptions() {
    return this.optionTargets.filter(option => !option.hidden)
  }
}
